import { ref, computed, watch } from 'vue';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useAuth } from '~/composables/useAuth';
import { useToast } from '#imports';

export type CartItemType = 'artwork' | 'set';

export interface CartItem {
  cartItemId: string;
  type: CartItemType;
  id: string | number; // UUID for artwork, integer for set
  slug?: string | null; // Available for artworks
  name: string;
  price: number;
  availability: string | null;
  image: string | null;
  artist?: string | null;
  size?: string | null;
  display_size?: string | null;
  weight?: string | number | null;
  is_work_framed?: string | null;
  framed_artwork_images?: string | null;
  qam_framing_repair_cost?: string | null;
  // Checkout asks about framing per item, so an artwork carries what that needs: the size the
  // calculator prices on, the eligibility fields, and the photo the live preview draws.
  size_in_inches?: string | null;
  framable?: string | null;
  artwork_category?: string | null;
  photo_name?: string | null;
  photo_width?: number | null;
  photo_height?: number | null;
  artworkIds?: string[];
  setArtworks?: any[];
  addedAt: string;
}

// Global UI state for the cart slideover & cart animation
const isCartOpen = ref(false);
const isCartBumping = ref(false);
let bumpTimeout: any = null;

export const useCart = () => {
  const queryClient = useQueryClient();
  const { isLoggedIn } = useAuth();
  const toast = useToast();

  const triggerCartBump = () => {
    if (isCartBumping.value) return;
    isCartBumping.value = true;
    if (bumpTimeout) clearTimeout(bumpTimeout);
    bumpTimeout = setTimeout(() => {
      isCartBumping.value = false;
    }, 1200);
  };

  // Clear cart cache when user logs out, or invalidate when they log in
  watch(isLoggedIn, (newVal, oldVal) => {
    if (newVal !== oldVal) {
      if (!newVal) {
        queryClient.removeQueries({ queryKey: ['cartItems'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['cartItems'] });
      }
    }
  });

  const toggleCart = () => {
    isCartOpen.value = !isCartOpen.value;
  };

  const openCart = () => {
    isCartOpen.value = true;
  };

  const closeCart = () => {
    isCartOpen.value = false;
  };

  // 1. Fetch Cart Items
  const { data, isPending: isLoadingCart, refetch: refetchCart } = useQuery({
    queryKey: ['cartItems'],
    queryFn: async () => {
      const response = await $fetch<{ cartItems: CartItem[] }>('/api/cart');
      return response.cartItems;
    },
    enabled: isLoggedIn, // Only fetch if user is logged in
    staleTime: 1000 * 60 * 5, // Cache for 5 mins
    retry: false, // Do not endlessly retry on 401 or 500 errors
  });

  const cartItems = computed(() => data.value || []);

  const cartTotal = computed(() => {
    return cartItems.value.reduce((total, item) => total + (item.price || 0), 0);
  });

  const cartItemCount = computed(() => cartItems.value.length);

  // 2. Add to Cart Mutation (Optimistic Update)
  const addMutation = useMutation({
    mutationFn: async ({ artworkId, setId }: { artworkId?: string; setId?: number }) => {
      return await $fetch('/api/cart/add', {
        method: 'POST',
        body: { artworkId, setId },
      });
    },
    onMutate: async (newItemArgs) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['cartItems'] });

      // Snapshot the previous value
      const previousItems = queryClient.getQueryData<CartItem[]>(['cartItems']) || [];

      return { previousItems };
    },
    onSuccess: (data, variables) => {
      if ((data as any).message) {
        // Already in cart
        toast.add({ title: "Already in Cart", description: (data as any).message });
      } else {
        triggerCartBump();
      }
    },
    onError: (err, variables, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(['cartItems'], context.previousItems);
      }
      toast.add({ title: "Error", description: "Failed to add to cart", color: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
    },
  });

  // 3. Remove from Cart Mutation (Optimistic Update)
  const removeMutation = useMutation({
    mutationFn: async (cartItemId: string) => {
      return await $fetch(`/api/cart/remove?cartItemId=${cartItemId}`, {
        method: 'DELETE',
      });
    },
    onMutate: async (cartItemId) => {
      await queryClient.cancelQueries({ queryKey: ['cartItems'] });
      const previousItems = queryClient.getQueryData<CartItem[]>(['cartItems']) || [];

      // Optimistically remove the item
      queryClient.setQueryData(
        ['cartItems'],
        previousItems.filter((item) => item.cartItemId !== cartItemId)
      );

      return { previousItems };
    },
    onError: (err, cartItemId, context) => {
      if (context?.previousItems) {
        queryClient.setQueryData(['cartItems'], context.previousItems);
      }
      toast.add({ title: "Error", description: "Failed to remove item", color: "error" });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['cartItems'] });
    },
  });

  const addToCart = (args: { artworkId?: string; setId?: number }) => {
    addMutation.mutate(args);
  };

  const removeFromCart = (cartItemId: string) => {
    removeMutation.mutate(cartItemId);
  };

  const clearLocalCart = () => {
    queryClient.setQueryData(['cartItems'], []);
  };

  return {
    isCartOpen,
    toggleCart,
    openCart,
    closeCart,
    cartItems,
    cartTotal,
    cartItemCount,
    isLoadingCart,
    addToCart,
    removeFromCart,
    clearLocalCart,
    refetchCart,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
    isCartBumping,
    triggerCartBump,
  };
};
